import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeftIcon, RefreshCwIcon, SearchIcon } from 'lucide-react';
import clsx from 'clsx';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Button, Input, Text } from '@/components/ui';
import { Select, SelectOption } from '@/components/ui/Select';
import { Spinner } from '@/components/Spinner';
import { useContractPortfolio } from '@/hooks/useContractPortfolio';
import {
  ContractPortfolio,
  ContractPortfolioRow,
  HealthStatus,
  UsageStatus,
} from '@/lib/api/reports';
import {
  alertClasses,
  Card,
  daysText,
  fmtDate,
  fmtHours,
  fmtPercent,
  Kpi,
} from './ContractConsumptionPage';

const HEALTH: Record<HealthStatus, { label: string; dot: string; order: number }> = {
  critical: { label: 'Crítico', dot: 'bg-error-default', order: 0 },
  warning: { label: 'Atenção', dot: 'bg-amber-500', order: 1 },
  healthy: { label: 'Saudável', dot: 'bg-brand-default', order: 2 },
};

const USAGE: Record<UsageStatus, { label: string; hint: string; className: string }> = {
  OVERUSED: {
    label: 'Acelerado',
    hint: 'Saldo acaba antes do fim: oportunidade de upsell',
    className: 'bg-amber-50 text-amber-800',
  },
  UNDERUSED: {
    label: 'Subutilizado',
    hint: 'Cliente usa pouco do contratado: risco na renovação',
    className: 'bg-error-soft-surface text-error-default',
  },
  ON_TRACK: {
    label: 'No ritmo',
    hint: 'Uso compatível com o tempo de contrato',
    className: 'bg-neutral-soft-surface text-neutral-default',
  },
  NO_HOURS_BANK: {
    label: 'Sem banco',
    hint: 'Contrato sem horas contratadas (só limite mensal)',
    className: 'bg-neutral-soft-surface text-neutral-muted',
  },
};

const STATUS_OPTIONS: SelectOption[] = [
  { value: 'active', content: 'Ativos' },
  { value: 'inactive', content: 'Inativos' },
  { value: 'all', content: 'Todos' },
];
const HEALTH_OPTIONS: SelectOption[] = [
  { value: 'all', content: 'Toda saúde' },
  ...(Object.keys(HEALTH) as HealthStatus[]).map((h) => ({ value: h, content: HEALTH[h].label })),
];
const USAGE_OPTIONS: SelectOption[] = [
  { value: 'all', content: 'Todo uso' },
  ...(Object.keys(USAGE) as UsageStatus[]).map((u) => ({ value: u, content: USAGE[u].label })),
];
const EXPIRY_OPTIONS: SelectOption[] = [
  { value: 'all', content: 'Qualquer vencimento' },
  { value: '30', content: 'Vence em até 30 dias' },
  { value: '60', content: 'Vence em até 60 dias' },
  { value: '90', content: 'Vence em até 90 dias' },
  { value: 'expired', content: 'Já vencidos' },
];

function matchesExpiry(r: ContractPortfolioRow, expiry: string) {
  if (expiry === 'all') return true;
  if (r.daysToEnd === null) return false;
  if (expiry === 'expired') return r.daysToEnd < 0;
  return r.daysToEnd >= 0 && r.daysToEnd <= Number(expiry);
}

// Worst health first, then whatever ends soonest
function byPriority(a: ContractPortfolioRow, b: ContractPortfolioRow) {
  const h = HEALTH[a.health].order - HEALTH[b.health].order;
  if (h !== 0) return h;
  return (a.daysToEnd ?? Infinity) - (b.daysToEnd ?? Infinity);
}

export function ContractPortfolioPage() {
  const { t } = useTranslation();
  const { data, isLoading, reload } = useContractPortfolio();
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('active');
  const [health, setHealth] = useState('all');
  const [usage, setUsage] = useState('all');
  const [expiry, setExpiry] = useState('all');

  const rows = useMemo(() => {
    if (!data) return [];
    const q = search.trim().toLowerCase();
    return data.contracts
      .filter(
        (r) =>
          (status === 'all' || r.isActive === (status === 'active')) &&
          (health === 'all' || r.health === health) &&
          (usage === 'all' || r.usage === usage) &&
          matchesExpiry(r, expiry) &&
          (!q || r.companyName.toLowerCase().includes(q) || r.number.toLowerCase().includes(q))
      )
      .sort(byPriority);
  }, [data, search, status, health, usage, expiry]);

  return (
    <div className="gap-xl flex flex-1 flex-col">
      <header className="gap-sm flex w-full flex-wrap items-center justify-between">
        <div className="gap-2xs flex items-center">
          <Link to="/reports" className="text-neutral-muted hover:text-neutral-default">
            <ArrowLeftIcon size={20} />
          </Link>
          <div>
            <Text as="h1" variant="heading">
              {t('reports.contractPortfolio.title')}
            </Text>
            {data && (
              <Text variant="label" size="xs" className="text-neutral-muted">
                Atualizado{' '}
                {formatDistanceToNow(new Date(data.generatedAt), { addSuffix: true, locale: ptBR })}
              </Text>
            )}
          </div>
        </div>
        <Button variant="secondary" size="sm" onClick={reload} disabled={isLoading}>
          <RefreshCwIcon size={18} className={clsx(isLoading && 'animate-spin')} />
          Atualizar
        </Button>
      </header>

      {!data && isLoading && (
        <div className="py-2xl flex justify-center">
          <Spinner color="black" />
        </div>
      )}

      {data && (
        <div className={clsx('gap-md flex flex-col', isLoading && 'opacity-60')}>
          <Kpis kpis={data.kpis} />
          <HealthBar kpis={data.kpis} />

          <section className="border-transparent-dark-12 bg-base-white p-md gap-xs flex flex-wrap items-center rounded-2xl border shadow-xs">
            <Input
              placeholder="Buscar cliente ou contrato"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              leftIcon={<SearchIcon size={18} />}
              className="w-[260px]"
            />
            <Select
              options={STATUS_OPTIONS}
              width={130}
              value={status}
              onValueChange={(v) => setStatus(v || 'active')}
            />
            <Select
              options={HEALTH_OPTIONS}
              width={150}
              value={health}
              onValueChange={(v) => setHealth(v || 'all')}
            />
            <Select
              options={USAGE_OPTIONS}
              width={150}
              value={usage}
              onValueChange={(v) => setUsage(v || 'all')}
            />
            <Select
              options={EXPIRY_OPTIONS}
              width={210}
              value={expiry}
              onValueChange={(v) => setExpiry(v || 'all')}
            />
            <Text variant="label" size="xs" className="text-neutral-muted ml-auto">
              {rows.length} de {data.contracts.length} contrato(s)
            </Text>
          </section>

          <Card>
            <PortfolioTable rows={rows} />
          </Card>

          <Text variant="label" size="xs" className="text-neutral-muted">
            Ritmo = % do contratado já usado ÷ % da vigência já decorrida. Abaixo de 0,5 depois de
            25% da vigência, o contrato é marcado como subutilizado. Clique numa linha para ver o
            consumo detalhado do contrato.
          </Text>
        </div>
      )}
    </div>
  );
}

function Kpis({ kpis }: { kpis: ContractPortfolio['kpis'] }) {
  const usedPercent =
    kpis.contractedHours > 0
      ? Math.round((kpis.consumedHours / kpis.contractedHours) * 1000) / 10
      : null;
  return (
    <div className="gap-md grid grid-cols-2 lg:grid-cols-5">
      <Kpi
        label="Contratos ativos"
        value={String(kpis.activeContracts)}
        hint={`de ${kpis.totalContracts} cadastrados`}
      />
      <Kpi
        label="Horas consumidas"
        value={fmtHours(kpis.consumedHours)}
        hint={`${fmtPercent(usedPercent)} de ${fmtHours(kpis.contractedHours)} contratadas`}
      />
      <Kpi
        label="Precisam de atenção"
        value={String(kpis.health.critical + kpis.health.warning)}
        hint={`${kpis.health.critical} crítico(s), ${kpis.health.warning} em atenção`}
      />
      <Kpi
        label="Renovações (90 dias)"
        value={String(kpis.expiring.d90)}
        hint={`${kpis.expiring.d30} em 30d · ${kpis.expiring.d60} em 60d · ${kpis.expiredStillActive} vencido(s) ainda ativo(s)`}
      />
      <Kpi
        label="Efetividade de uso"
        value={`${kpis.usage.overused} / ${kpis.usage.underused}`}
        hint="acelerados (upsell) / subutilizados (risco)"
      />
    </div>
  );
}

function HealthBar({ kpis }: { kpis: ContractPortfolio['kpis'] }) {
  const total = kpis.health.critical + kpis.health.warning + kpis.health.healthy;
  if (total === 0) return null;
  const parts = (['critical', 'warning', 'healthy'] as HealthStatus[]).map((h) => ({
    h,
    n: kpis.health[h],
  }));
  return (
    <div>
      <div className="bg-neutral-subtle flex h-2.5 overflow-hidden rounded-full">
        {parts.map(({ h, n }) =>
          n > 0 ? (
            <div key={h} className={HEALTH[h].dot} style={{ width: `${(n / total) * 100}%` }} />
          ) : null
        )}
      </div>
      <div className="gap-md mt-4xs flex">
        {parts.map(({ h, n }) => (
          <span key={h} className="gap-4xs flex items-center">
            <span className={clsx('inline-block size-2.5 rounded-full', HEALTH[h].dot)} />
            <Text variant="label" size="xs" className="text-neutral-muted">
              {HEALTH[h].label}: {n}
            </Text>
          </span>
        ))}
      </div>
    </div>
  );
}

function projectionShort(r: ContractPortfolioRow) {
  switch (r.projection.status) {
    case 'EXCEEDED':
      return 'Esgotado';
    case 'NO_CONTRACTED_HOURS':
      return '-';
    case 'NO_RECENT_CONSUMPTION':
      return 'Sem consumo recente';
    case 'LASTS_UNTIL_END':
      return 'Dura até o fim';
    default:
      return `Acaba ~${fmtDate(r.projection.depletionDate)}`;
  }
}

const HEADERS = [
  'Saúde',
  'Cliente / contrato',
  'Vigência',
  'Consumo',
  'Saldo',
  'Ritmo/mês',
  'Projeção',
  'Uso',
  'Alertas',
];

function PortfolioTable({ rows }: { rows: ContractPortfolioRow[] }) {
  if (rows.length === 0) {
    return <Text className="text-neutral-muted">Nenhum contrato com esses filtros.</Text>;
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse">
        <thead>
          <tr>
            {HEADERS.map((h) => (
              <th
                key={h}
                className="bg-neutral-soft-surface border-neutral-subtle px-xs py-2xs border-b text-left"
              >
                <Text variant="label" size="xs" strong className="text-neutral-strong uppercase">
                  {h}
                </Text>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <Row key={r.id} r={r} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Row({ r }: { r: ContractPortfolioRow }) {
  const href = `/reports/contract-consumption?contractId=${encodeURIComponent(r.id)}`;
  const percent = Math.min(100, Math.max(0, r.percentUsed ?? 0));
  const worstAlert = r.alerts.find((a) => a.severity === 'critical') ?? r.alerts[0];
  const cell = 'border-neutral-subtle px-xs py-2xs border-b align-middle';

  return (
    <tr className={clsx('hover:bg-neutral-soft-surface', !r.isActive && 'opacity-60')}>
      <td className={cell}>
        <span className="gap-4xs flex items-center whitespace-nowrap">
          <span className={clsx('inline-block size-2.5 rounded-full', HEALTH[r.health].dot)} />
          <Text size="xs">{HEALTH[r.health].label}</Text>
        </span>
      </td>
      <td className={cell}>
        <Link to={href} className="hover:underline">
          <Text strong>{r.companyName}</Text>
          <Text variant="label" size="xs" className="text-neutral-muted">
            {r.number}
            {!r.isActive && ' · inativo'}
          </Text>
        </Link>
      </td>
      <td className={clsx(cell, 'whitespace-nowrap')}>
        <Text>{fmtDate(r.endDate)}</Text>
        <Text variant="label" size="xs" className="text-neutral-muted">
          {daysText(r.daysToEnd)}
        </Text>
      </td>
      <td className={clsx(cell, 'min-w-[150px]')}>
        {r.percentUsed === null ? (
          <Text>{fmtHours(r.consumedHours)}</Text>
        ) : (
          <>
            <Text size="xs">
              {fmtHours(r.consumedHours)} / {fmtHours(r.contractedHours)} (
              {fmtPercent(r.percentUsed)})
            </Text>
            <div className="bg-neutral-subtle mt-4xs h-1.5 overflow-hidden rounded-full">
              <div
                className={clsx(
                  'h-full rounded-full',
                  r.projection.status === 'EXCEEDED' ? 'bg-error-default' : 'bg-brand-default'
                )}
                style={{ width: `${percent}%` }}
              />
            </div>
          </>
        )}
      </td>
      <td className={clsx(cell, 'text-right whitespace-nowrap')}>
        <Text className={clsx((r.remainingHours ?? 0) < 0 && 'text-error-default')}>
          {fmtHours(r.remainingHours)}
        </Text>
      </td>
      <td className={clsx(cell, 'text-right whitespace-nowrap')}>
        <Text>{fmtHours(r.projection.burnRateHours)}</Text>
      </td>
      <td className={clsx(cell, 'whitespace-nowrap')}>
        <Text size="xs">{projectionShort(r)}</Text>
      </td>
      <td className={cell}>
        <span
          title={USAGE[r.usage].hint}
          className={clsx(
            'px-2xs py-4xs inline-block rounded-md whitespace-nowrap',
            USAGE[r.usage].className
          )}
        >
          <Text size="xs" strong className="text-inherit">
            {USAGE[r.usage].label}
            {r.pace !== null && ` · ${r.pace.toLocaleString('pt-BR')}`}
          </Text>
        </span>
      </td>
      <td className={clsx(cell, 'max-w-[240px]')}>
        {worstAlert ? (
          <span
            className={clsx(
              'px-2xs py-4xs inline-block rounded-md',
              alertClasses[worstAlert.severity]
            )}
          >
            <Text size="xs" className="text-inherit">
              {worstAlert.message}
              {r.alerts.length > 1 && ` (+${r.alerts.length - 1})`}
            </Text>
          </span>
        ) : (
          <Text className="text-neutral-muted">-</Text>
        )}
      </td>
    </tr>
  );
}
